/**
 * Elapsed seconds since an instant, ticking once a second. SPEC.md §16.5, §44.
 *
 * One interval for the whole app, started on the first subscriber and stopped
 * with the last, so an all-connected table pays nothing for it. Three components
 * previously owned a timer each, which also meant three unsynchronised second
 * boundaries on one screen. §44
 */

import { useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();
let now = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    now = Date.now();
    timer = setInterval(() => {
      now = Date.now();
      for (const l of [...listeners]) l();
    }, 1_000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function snapshot(): number {
  return now;
}

/**
 * `startedAt` is an ISO timestamp (a player's `lastSeenAt`) or epoch ms (this
 * device's own `offlineSince`). An unparseable value counts from now rather than
 * printing a nonsense duration.
 */
export function useSecondsSince(startedAt: string | number): number {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  const from = typeof startedAt === 'number' ? startedAt : Date.parse(startedAt);
  if (Number.isNaN(from)) return 0;
  return Math.max(0, Math.floor((current - from) / 1_000));
}
